import React from "react";
import GhostFibers from "./GhostFibers";
import { supabase } from "./supabaseClient";

// The screen an APPROVED seller sees before their dashboard opens: a welcome
// message and six boxes for the verification code that INUA emailed them.
//
// The code is checked by the database (the `verify_seller_access_code`
// function), never in the browser, so it can't be read out of the page or
// skipped. After 5 wrong tries the database locks it until INUA issues a new
// code. Once it passes, `access_verified_at` is stamped on the application and
// this screen never appears again for that seller.

export const SELLERS_EMAIL = "sellers@inuamarketplace.com";
const CODE_LENGTH = 6;

// "brooklynleo526@gmail.com" -> "br••••••••@gmail.com"
export function maskEmail(email: string) {
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const keep = name.slice(0, Math.min(2, name.length));
  return `${keep}${"•".repeat(Math.max(name.length - keep.length, 3))}@${domain}`;
}

export default function SellerAccessGate({
  maskedEmail,
  onVerified,
  onBack,
}: {
  maskedEmail?: string | null;
  onVerified: () => void;
  onBack: () => void;
}) {
  const [digits, setDigits] = React.useState<string[]>(
    Array(CODE_LENGTH).fill("")
  );
  const [verifying, setVerifying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [locked, setLocked] = React.useState(false);
  const [shakeKey, setShakeKey] = React.useState(0);
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);

  React.useEffect(() => {
    const t = setTimeout(() => refs.current[0]?.focus(), 350);
    return () => clearTimeout(t);
  }, []);

  const focusBox = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))];
    el?.focus();
    el?.select();
  };

  const submit = async (code: string) => {
    if (verifying || locked) return;
    setVerifying(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "verify_seller_access_code",
        { p_code: code }
      );
      if (rpcError) throw rpcError;

      if (data?.ok) {
        onVerified();
        return; // stay "verifying" — the tile transition takes over
      }

      setDigits(Array(CODE_LENGTH).fill(""));
      setShakeKey((k) => k + 1);
      switch (data?.reason) {
        case "wrong": {
          const left = Number(data?.attempts_left ?? 0);
          setError(
            left > 0
              ? `Invalid code. ${left} ${
                  left === 1 ? "try" : "tries"
                } left.`
              : "Invalid code, you are out of tries."
          );
          if (left <= 0) setLocked(true);
          break;
        }
        case "locked":
          setLocked(true);
          setError("Too many wrong tries. Please contact us for a new code.");
          break;
        case "no_code":
          setError(
            "Your code hasn't been issued yet. Check your email, or contact us."
          );
          break;
        case "not_approved":
          setError("Your application isn't approved yet.");
          break;
        default:
          setError("We couldn't verify that code. Please try again.");
      }
      if (data?.reason !== "locked") focusBox(0);
    } catch (e: any) {
      console.error("Code verification failed:", e);
      setDigits(Array(CODE_LENGTH).fill(""));
      setError(
        navigator.onLine === false
          ? "You're offline. Check your connection and try again."
          : "We couldn't reach the server. Please try again."
      );
      focusBox(0);
    } finally {
      setVerifying(false);
    }
  };

  // Put `chars` into the boxes starting at `start` (typing, paste, autofill).
  const fillFrom = (start: number, chars: string) => {
    const next = [...digits];
    for (let k = 0; k < chars.length && start + k < CODE_LENGTH; k++) {
      next[start + k] = chars[k];
    }
    setDigits(next);
    focusBox(Math.min(start + chars.length, CODE_LENGTH - 1));
    if (next.every((d) => d !== "")) submit(next.join(""));
  };

  const handleChange = (i: number, raw: string) => {
    let v = raw.replace(/\D/g, "");
    if (!v) {
      const next = [...digits];
      next[i] = "";
      setDigits(next);
      return;
    }
    // Typing a new digit into a box that already has one: keep the new one.
    if (v.length === 2 && digits[i] !== "") v = v.slice(-1);
    fillFrom(i, v);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && digits[i] === "" && i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = "";
      setDigits(next);
      focusBox(i - 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(i + 1);
    } else if (e.key === "Enter" && digits.every((d) => d !== "")) {
      submit(digits.join(""));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    fillFrom(0, pasted.slice(0, CODE_LENGTH));
  };

  const complete = digits.every((d) => d !== "");

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0D0917] p-6 text-center">
      <style>{`
        @keyframes gate-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
      `}</style>

      <div className="absolute inset-0 z-0">
        <GhostFibers
          lineColor="#1006c5"
          glowColor="#ffb800"
          speed={0.2}
          scale={2}
          rotation={0}
          rotationSpeed={0.25}
          layers={4}
          waveAmplitude={0.015}
          waveFrequency={3}
          waveSpeed={0.15}
          layerSpeed={0.08}
          twist={0.1}
          twistFrequency={5}
          twistSpeed={1.2}
          lineFrequency={5}
          lineSpacing={2}
          lineSharpness={16}
          glowFalloff={10}
          glowIntensity={1.6}
          brightness={2}
          blueBoost={1.25}
          vignette={0.8}
          grain={0.05}
          dpr={1}
          lightMode={false}
          fps={60}
          paused={false}
        />
      </div>

      <button
        onClick={onBack}
        className="absolute left-6 top-6 z-10 flex items-center gap-1.5 text-sm font-semibold text-white"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        Back
      </button>

      <div className="relative z-10 w-full max-w-md rounded-xl border border-white/10 bg-black/40 p-6 shadow-2xl shadow-black/50 backdrop-blur-xl sm:p-8">
        <div className="mb-4 flex justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FFB800]/20">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFB800"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-7 w-7"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </div>

        <h2 className="mb-2 text-2xl font-bold text-white">
          Welcome to INUA!
        </h2>
        <p className="mb-1 text-white">
          Congratulations, your application has been approved!
        </p>
        <p className="mb-6 text-sm leading-relaxed text-white/70">
          To open your seller dashboard, enter the 6-digit verification code we
          sent to your email
          {maskedEmail ? (
            <>
              {" "}
              (<span className="font-semibold text-white">{maskedEmail}</span>)
            </>
          ) : null}
          .
        </p>

        <div
          key={shakeKey}
          onPaste={handlePaste}
          className="mb-3 flex justify-center gap-2 sm:gap-3"
          style={shakeKey ? { animation: "gate-shake 0.4s" } : undefined}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
              value={d}
              disabled={verifying || locked}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onFocus={(e) => e.currentTarget.select()}
              className={`aspect-[4/5] min-w-0 max-w-[52px] flex-1 rounded-xl border bg-white/5 text-center text-xl font-bold text-white outline-none transition-colors focus:border-[#FFB800] disabled:opacity-60 ${
                error ? "border-[#FF3B5C]" : "border-white/20"
              }`}
            />
          ))}
        </div>

        <p
          className="mb-4 min-h-[1.25rem] text-xs font-medium text-[#FF3B5C]"
          role="alert"
        >
          {error}
        </p>

        <button
          type="button"
          onClick={() => submit(digits.join(""))}
          disabled={!complete || verifying || locked}
          className="w-full rounded-xl bg-[#FFB800] py-3.5 font-bold text-black transition-opacity disabled:opacity-40"
        >
          {verifying ? "Verifying…" : "Verify & Enter"}
        </button>

        <p className="mt-5 text-[11px] leading-relaxed text-white/60">
          Can&apos;t find the email? Check your spam folder, or write to{" "}
          <a
            href={`mailto:${SELLERS_EMAIL}`}
            className="font-semibold text-[#FFB800] underline underline-offset-2"
          >
            {SELLERS_EMAIL}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
